/**
 * GA4 `error_message` tracking.
 *
 * Unlike the other generic UI events, `error_message` is NOT click-based and does
 * not flow through the delegated `plugins/track-clicks.client.ts` listener — errors
 * do not bubble through `click`. Instead this composable is invoked explicitly from
 * the places that surface a user-facing error:
 *   - `stores/snackbar.ts` → `showMessage({ color: 'error', textKey })` (single source
 *     for all transient snackbar errors)
 *   - `error.vue` (route-level 404 / 5xx / generic)
 *   - failed form submits (`login`, `forgot-password`, …) that do not go through the snackbar
 *
 * `error_text` always carries the stable i18n KEY (locale-independent), never the
 * rendered/localized message, so GA4 dimensions stay consistent across de/en/fr.
 */

export type ErrorType = 'validation' | 'network' | 'business' | 'not_found' | 'server'

export interface TrackErrorPayload {
  /** GA4 error category. */
  type: ErrorType
  /** Stable i18n key, e.g. `errors.login.failed`. */
  text: string
}

export function useErrorTracking() {
  const gtm = useGtm()

  function trackError({ type, text }: TrackErrorPayload): void {
    gtm?.trackEvent({
      event: 'error_message',
      error_type: type,
      error_text: text,
      page_section: usePageSection(),
    })
  }

  return { trackError }
}
